import React, { useRef, useState, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
} from "chart.js";
import { Scatter } from "react-chartjs-2";
import annotationPlugin from "chartjs-plugin-annotation";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin,
);

interface ChartOrder {
  price: number;
  amount: number;
  id: string;
  side?: "buy" | "sell" | "long" | "short";
  orderId?: string;
  isPending?: boolean;
}

interface PriceAmountChartProps {
  minPrice: number;
  maxPrice: number;
  orders: ChartOrder[];
  pendingOrders?: ChartOrder[];
  onAddOrder: (price: number, amount: number) => void;
  onRemoveOrder: (id: string) => void;
  currentPrice: number | null;
  markPrice: number | null;
}

export const PriceAmountChart: React.FC<PriceAmountChartProps> = ({
  minPrice,
  maxPrice,
  orders,
  pendingOrders = [],
  onAddOrder,
  onRemoveOrder,
  currentPrice,
  markPrice,
}) => {
  const chartRef = useRef<ChartJS<"scatter">>(null);
  const [hoveredOrder, setHoveredOrder] = useState<string | null>(null);

  // Calculate max amount for scaling
  const maxAmount = useMemo(() => {
    const allOrders = [...orders, ...pendingOrders];
    return allOrders.length > 0
      ? Math.max(...allOrders.map((o) => o.amount), 0.1)
      : 0.1;
  }, [orders, pendingOrders]);

  // Prepare data for Chart.js scatter chart
  const chartData: ChartData<"scatter"> = useMemo(() => {
    const regularOrdersData = orders.map((order) => ({
      x: order.amount,
      y: order.price,
      id: order.id,
      side: order.side,
      isPending: false,
    }));

    const pendingOrdersData = pendingOrders.map((order) => ({
      x: order.amount,
      y: order.price,
      id: order.id,
      side: order.side,
      isPending: true,
    }));

    return {
      datasets: [
        // Regular orders - Long (blue)
        {
          label: "Long Orders",
          data: regularOrdersData.filter((d) => {
            const order = orders.find((o) => o.id === d.id);
            return (
              order?.side === "long" || order?.side === "buy" || !order?.side
            );
          }),
          backgroundColor: "rgba(77, 171, 247, 0.8)",
          borderColor: "#fff",
          borderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 10,
        },
        // Regular orders - Short (orange)
        {
          label: "Short Orders",
          data: regularOrdersData.filter((d) => {
            const order = orders.find((o) => o.id === d.id);
            return order?.side === "short" || order?.side === "sell";
          }),
          backgroundColor: "rgba(255, 152, 0, 0.8)",
          borderColor: "#fff",
          borderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 10,
        },
        // Pending orders - Long (green)
        {
          label: "Pending Long",
          data: pendingOrdersData.filter((d) => {
            const order = pendingOrders.find((o) => o.id === d.id);
            return order?.side === "long" || order?.side === "buy";
          }),
          backgroundColor: "rgba(40, 167, 69, 0.8)",
          borderColor: "#fff",
          borderWidth: 2,
          borderDash: [3, 3],
          pointRadius: 6,
          pointHoverRadius: 10,
        },
        // Pending orders - Short (red)
        {
          label: "Pending Short",
          data: pendingOrdersData.filter((d) => {
            const order = pendingOrders.find((o) => o.id === d.id);
            return order?.side === "short" || order?.side === "sell";
          }),
          backgroundColor: "rgba(220, 53, 69, 0.8)",
          borderColor: "#fff",
          borderWidth: 2,
          borderDash: [3, 3],
          pointRadius: 6,
          pointHoverRadius: 10,
        },
      ],
    };
  }, [orders, pendingOrders]);

  // Chart options with annotation plugin configuration
  const options: ChartOptions<"scatter"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      backgroundColor: "#1a1a1a",
      plugins: {
        legend: {
          display: true,
          position: "top" as const,
          labels: {
            color: "#fff",
            font: {
              size: 12,
            },
          },
        },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (context) => {
              const point = context.raw as {
                x: number;
                y: number;
                id?: string;
              };
              const order = [...orders, ...pendingOrders].find(
                (o) => o.id === point.id,
              );
              if (order) {
                return [
                  `Price: ${order.price.toFixed(2)} USDC`,
                  `Amount: ${order.amount.toFixed(4)} BTC`,
                  order.isPending
                    ? `Pending ${order.side === "long" || order.side === "buy" ? "Long" : "Short"}`
                    : "",
                ].filter(Boolean);
              }
              return `Price: ${point.y.toFixed(2)}, Amount: ${point.x.toFixed(4)}`;
            },
          },
        },
        annotation: {
          annotations: {
            // Current price line (green dashed)
            ...(currentPrice !== null &&
            currentPrice >= minPrice &&
            currentPrice <= maxPrice
              ? {
                  currentPriceLine: {
                    type: "line",
                    xMin: 0,
                    xMax: maxAmount,
                    yMin: currentPrice,
                    yMax: currentPrice,
                    borderColor: "#00ff00",
                    borderWidth: 2,
                    borderDash: [5, 5],
                    label: {
                      display: true,
                      content: `Current: ${currentPrice.toLocaleString()}`,
                      position: "end",
                      backgroundColor: "rgba(0, 255, 0, 0.8)",
                      color: "#000",
                      font: {
                        size: 12,
                        weight: "bold",
                        family: "monospace",
                      },
                      padding: {
                        left: 5,
                        right: 5,
                        top: 2,
                        bottom: 2,
                      },
                    },
                    xScaleID: "x",
                    yScaleID: "y",
                  },
                }
              : {}),
            // Mark price line (red solid) with right-pointing arrow
            ...(markPrice !== null &&
            markPrice >= minPrice &&
            markPrice <= maxPrice
              ? {
                  markPriceLine: {
                    type: "line",
                    xMin: 0,
                    xMax: maxAmount,
                    yMin: markPrice,
                    yMax: markPrice,
                    borderColor: "#ff0000",
                    borderWidth: 2,
                    label: {
                      display: true,
                      content: `Mark Price: ${markPrice.toLocaleString()}`,
                      position: "start",
                      backgroundColor: "rgba(255, 0, 0, 0.8)",
                      color: "#fff",
                      font: {
                        size: 12,
                        weight: "bold",
                        family: "monospace",
                      },
                      padding: {
                        left: 5,
                        right: 5,
                        top: 2,
                        bottom: 2,
                      },
                    },
                    xScaleID: "x",
                    yScaleID: "y",
                    arrowHeads: {
                      start: {
                        enabled: false,
                        fill: false,
                        length: 0,
                        width: 0,
                      },
                      end: {
                        enabled: true,
                        fill: true,
                        length: 15,
                        width: 10,
                        backgroundColor: "#ff0000",
                        borderColor: "#ff0000",
                        borderWidth: 2,
                      },
                    },
                  },
                }
              : {}),
          },
        },
      },
      scales: {
        x: {
          type: "linear",
          position: "bottom",
          title: {
            display: true,
            text: "Amount (BTC)",
            color: "#fff",
            font: {
              size: 14,
              weight: "bold",
            },
          },
          min: 0,
          max: maxAmount,
          ticks: {
            color: "#888",
            font: {
              size: 12,
              family: "monospace",
            },
            callback: function (value) {
              return Number(value).toFixed(4);
            },
          },
          grid: {
            color: "#333",
          },
        },
        y: {
          type: "linear",
          title: {
            display: true,
            text: "Price (USDC)",
            color: "#fff",
            font: {
              size: 14,
              weight: "bold",
            },
          },
          min: minPrice,
          max: maxPrice,
          ticks: {
            color: "#888",
            font: {
              size: 12,
              family: "monospace",
            },
            callback: function (value) {
              return Number(value).toLocaleString();
            },
          },
          grid: {
            color: "#333",
          },
        },
      },
      onClick: (event: any, _activeElements: any[]) => {
        if (!chartRef.current) return;

        const chart = chartRef.current;
        const canvas = chart.canvas;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const nativeEvent = event.native as MouseEvent | null;
        if (!nativeEvent) return;

        const x = nativeEvent.clientX - rect.left;
        const y = nativeEvent.clientY - rect.top;

        // Get the scales
        const xScale = chart.scales.x;
        const yScale = chart.scales.y;

        // Convert pixel coordinates to data values
        const amount = xScale.getValueForPixel(x);
        const price = yScale.getValueForPixel(y);

        if (
          amount === null ||
          amount === undefined ||
          price === null ||
          price === undefined
        )
          return;

        // Check if clicking on an existing point
        const allOrders = [...orders, ...pendingOrders];
        const clickedOrder = allOrders.find((order) => {
          const pointX = xScale.getPixelForValue(order.amount);
          const pointY = yScale.getPixelForValue(order.price);
          const distance = Math.sqrt(
            Math.pow(x - pointX, 2) + Math.pow(y - pointY, 2),
          );
          return distance <= 12; // Click radius
        });

        if (clickedOrder) {
          // Remove order if clicking on existing one
          onRemoveOrder(clickedOrder.id);
        } else {
          // Add new order
          const clampedPrice = Math.max(minPrice, Math.min(maxPrice, price));
          const clampedAmount = Math.max(0.001, Math.min(1.0, amount));

          onAddOrder(clampedPrice, clampedAmount);
        }
      },
      onHover: (event: any, _activeElements: any[]) => {
        if (!chartRef.current) return;

        const chart = chartRef.current;
        const canvas = chart.canvas;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const nativeEvent = event.native as MouseEvent | null;
        if (!nativeEvent) return;

        const x = nativeEvent.clientX - rect.left;
        const y = nativeEvent.clientY - rect.top;

        // Find hovered order
        const allOrders = [...orders, ...pendingOrders];
        const xScale = chart.scales.x;
        const yScale = chart.scales.y;

        const hovered = allOrders.find((order) => {
          const pointX = xScale.getPixelForValue(order.amount);
          const pointY = yScale.getPixelForValue(order.price);
          const distance = Math.sqrt(
            Math.pow(x - pointX, 2) + Math.pow(y - pointY, 2),
          );
          return distance <= 10; // Hover radius
        });

        setHoveredOrder(hovered?.id || null);
      },
    }),
    [
      minPrice,
      maxPrice,
      maxAmount,
      orders,
      pendingOrders,
      onAddOrder,
      onRemoveOrder,
      currentPrice,
      markPrice,
    ],
  );

  return (
    <div
      className="price-amount-chart-container"
      style={{ height: "600px", position: "relative" }}
    >
      <div
        style={{
          backgroundColor: "#1a1a1a",
          borderRadius: "4px",
          border: "1px solid #333",
          padding: "10px",
          height: "100%",
        }}
      >
        <Scatter
          ref={chartRef}
          data={chartData}
          options={options}
          style={{
            cursor: hoveredOrder ? "pointer" : "crosshair",
          }}
        />
      </div>
    </div>
  );
};
